/*
  Cart drawer behaviour. Markup: snippets/cart-drawer.liquid. Styles:
  component-cart-drawer.css.

  Modelled on rhodeskin.com's drawer:
  - It opens once an add has come back, and on a click of the header cart icon,
    which also refreshes it.
  - It re-renders from the server after every change. Rhode re-reads /cart.json;
    this uses the Section Rendering API.
  - It dims itself while a request is in flight.
  - The X, the dimmer and Escape all close it.

  Fixed on the way (rhode's own bugs):
  - The dim is tied to the request rather than a 5-8s timer.
  - An add error doesn't lock the cart.
  - Escape in the "what's covered" pop-up closes only the pop-up.
  - Focus goes back to whatever opened the drawer.

  The Dawn product form talks to this element through getSectionsToRender,
  setActiveElement and renderContents, the same contract Dawn's drawer had.
*/

const CC_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
const CC_FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.cc__panel');
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onOptionKeydown(event));
    document.addEventListener('keydown', (event) => this.onDocumentKeydown(event));
    this.bindCartIcon();

    // Back from checkout: take the protection line out again, as Seel does.
    this.dropProtection();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh().then(() => this.dropProtection());
    });

    // /cart lands on the home page with ?cart=open (layout/theme.liquid).
    const params = new URLSearchParams(window.location.search);
    if (params.get('cart') === 'open') {
      params.delete('cart');
      const query = params.toString();
      history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      this.open();
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  bindCartIcon() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.refresh();
      this.open(link);
    });
    link.addEventListener('keydown', (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        this.refresh();
        this.open(link);
      }
    });
  }

  /* ---- open / close ---------------------------------------------------- */

  open(trigger) {
    if (trigger) this.activeElement = trigger;
    if (this.isOpen) return;
    this.freeze();
    this.classList.add('is-open');
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      this.querySelector('.cc__close')?.focus({ preventScroll: true });
    }, 250);
  }

  close() {
    if (!this.isOpen) return;
    clearTimeout(this.focusTimer);
    this.closePicker();
    this.classList.remove('is-open');
    this.thaw();
    const trigger = this.activeElement;
    this.activeElement = null;
    if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
  }

  // The body is pinned in place rather than overflow-hidden, which iOS ignores.
  freeze() {
    this.scrollY = window.scrollY;
    const body = document.body;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.top = `-${this.scrollY}px`;
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    body.classList.add('cc-frozen');
  }

  thaw() {
    const body = document.body;
    const html = document.documentElement;
    body.classList.remove('cc-frozen');
    body.style.top = '';
    body.style.paddingRight = '';
    const behaviour = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.scrollY || 0);
    html.style.scrollBehavior = behaviour;
  }

  /* ---- Dawn product-form contract --------------------------------------- */

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(parsedState) {
    this.render(parsedState.sections);
    this.open();
  }

  /* ---- rendering -------------------------------------------------------- */

  render(sections) {
    if (!sections) return;
    const parser = new DOMParser();

    if (sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const next = doc.getElementById('CartDrawer');
      const current = this.querySelector('#CartDrawer');
      if (next && current) {
        const focused = current.contains(document.activeElement) ? document.activeElement.dataset.ccFocus : null;
        current.replaceWith(next);
        if (focused && this.isOpen) {
          const target = next.querySelector(`[data-cc-focus="${CSS.escape(focused)}"]`) || next.querySelector('.cc__close');
          target?.focus({ preventScroll: true });
        }
      }
      const root = doc.querySelector('cart-drawer');
      if (root) this.classList.toggle('is-empty', root.classList.contains('is-empty'));
    }

    if (sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      const inner = parser
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (icon && inner) icon.innerHTML = inner.innerHTML;
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${CC_SECTIONS.join(',')}`);
      this.render(await response.json());
    } catch (error) {
      console.error(error);
    }
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
  }

  // POSTs to a cart endpoint with the drawer's sections attached, then renders
  // the answer. Throws Shopify's error body (status, description) on failure.
  async request(url, body, { quiet = false } = {}) {
    if (!quiet) this.setLoading(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: CC_SECTIONS, sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw state;
      this.render(state.sections);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
      }
      return state;
    } finally {
      if (!quiet) this.setLoading(false);
    }
  }

  showError(message) {
    const box = this.querySelector('.cc__error');
    if (box) box.textContent = message || 'Something went wrong. Please try again.';
  }

  lineError(line, message) {
    const box = line?.querySelector('.cc__line-error');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
    clearTimeout(box.timer);
    box.timer = setTimeout(() => {
      box.hidden = true;
    }, 4000);
  }

  /* ---- clicks ----------------------------------------------------------- */

  onClick(event) {
    const target = event.target.closest(
      '[data-cc-close], [data-cc-qty], [data-cc-swatch-toggle], [data-cc-popup-close], [data-cc-option], [data-cc-add], [data-cc-protect], [data-cc-info], [data-cc-modal-close], [data-cc-checkout]'
    );
    if (!target || !this.contains(target)) return;

    if (target.matches('[data-cc-close]')) return this.close();
    if (target.matches('[data-cc-qty]')) return this.changeQuantity(target);
    if (target.matches('[data-cc-swatch-toggle]')) return this.togglePicker(target);
    if (target.matches('[data-cc-popup-close]')) return this.closePicker();
    if (target.matches('[data-cc-option]')) return this.selectOption(target);
    if (target.matches('[data-cc-add]')) return this.addUpsell(target);
    if (target.matches('[data-cc-protect]')) return this.checkoutWithProtection(target);
    if (target.matches('[data-cc-info]')) return this.querySelector('[data-cc-modal]')?.showModal();
    if (target.matches('[data-cc-modal-close]')) return target.closest('dialog')?.close();
    if (target.matches('[data-cc-checkout]') && target.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      if (this.querySelector('dialog[open]')) return; // the dialog closes itself
      if (this.querySelector('.cc__swatch.is-open')) return this.closePicker(true);
      return this.close();
    }
    if (event.key === 'Tab') this.trapTab(event);
  }

  trapTab(event) {
    if (this.querySelector('dialog[open]')) return;
    const focusable = [...this.panel.querySelectorAll(CC_FOCUSABLE)].filter(
      (element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---- quantity --------------------------------------------------------- */

  // Minus at 1 removes the line, with no confirmation, as on rhode.
  async changeQuantity(button) {
    const line = button.closest('[data-cc-line]');
    if (!line) return;
    const current = Number(line.dataset.quantity) || 0;
    const next = button.dataset.ccQty === 'plus' ? current + 1 : Math.max(current - 1, 0);
    const max = Number(this.dataset.maxQty) || 0;
    if (max && next > max) return this.lineError(line, `Limit ${max} per customer`);
    try {
      await this.request(window.routes.cart_change_url, { id: line.dataset.key, quantity: next });
    } catch (error) {
      this.lineError(this.querySelector(`[data-key="${CSS.escape(line.dataset.key)}"]`) || line, error.description || error.message);
    }
  }

  /* ---- add-on card ------------------------------------------------------ */

  togglePicker(trigger) {
    const swatch = trigger.closest('.cc__swatch');
    const opening = !swatch.classList.contains('is-open');
    this.closePicker();
    if (!opening) return;
    swatch.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    const selected = swatch.querySelector('[data-cc-option][data-selected="true"]') || swatch.querySelector('[data-cc-option]');
    setTimeout(() => selected?.focus({ preventScroll: true }), 50);
  }

  closePicker(restoreFocus = false) {
    this.querySelectorAll('.cc__swatch.is-open').forEach((swatch) => {
      swatch.classList.remove('is-open');
      const trigger = swatch.querySelector('[data-cc-swatch-toggle]');
      trigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    });
  }

  onOptionKeydown(event) {
    const option = event.target.closest?.('[data-cc-option]');
    if (!option || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    this.selectOption(option);
  }

  // Picking a variant swaps the label, price and picture in place, then closes.
  selectOption(option) {
    const card = option.closest('[data-cc-upsell]');
    if (!card) return;
    card.querySelectorAll('[data-cc-option]').forEach((item) => {
      const on = item === option;
      item.dataset.selected = on;
      item.setAttribute('aria-pressed', on);
    });
    card.querySelectorAll('[data-cc-upsell-label]').forEach((element) => {
      element.textContent = option.dataset.title;
    });
    card.querySelectorAll('[data-cc-upsell-price]').forEach((element) => {
      element.textContent = option.dataset.price;
    });
    const add = card.querySelector('[data-cc-add]');
    if (add) add.dataset.variantId = option.dataset.variantId;
    const image = card.querySelector('[data-cc-upsell-img] img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
    this.closePicker(true);
  }

  async addUpsell(button) {
    try {
      await this.request(window.routes.cart_add_url, {
        items: [
          {
            id: Number(button.dataset.variantId),
            quantity: 1,
            properties: { _attribution: 'Cart drawer add-on' },
          },
        ],
      });
    } catch (error) {
      this.showError(error.description || error.message);
    }
  }

  /* ---- package protection ----------------------------------------------- */

  async checkoutWithProtection(button) {
    try {
      await this.request(window.routes.cart_add_url, {
        items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
      });
      window.location.href = button.dataset.checkoutUrl;
    } catch (error) {
      this.showError(error.description || error.message);
    }
  }

  async dropProtection() {
    const key = this.querySelector('#CartDrawer')?.dataset.protectionKey;
    if (!key) return;
    try {
      await this.request(window.routes.cart_change_url, { id: key, quantity: 0 }, { quiet: true });
    } catch (error) {
      console.error(error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
